import type { CardType, WebSockets } from '@pushflow/types'
import { EAction } from '@pushflow/types/poker/enums'
import { clsx } from 'clsx'
import { AnimatePresence } from 'framer-motion'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeferredQueueValue } from '../../../hooks'
import { userSettingsAtom } from '../../../store/atoms'
import type { EHandName, ERound } from '../../../types/enums'
import { evaluateCards } from '../../../utils/evaluateCards'
import { Avatar, Card, CountdownBar } from '../../atoms'
import { AnimatedCard, Money } from '../../molecules'
import * as styles from './index.css'

export const TablePlayer = (
  props: {
    name: string | null
    avatar: string | null
    isCurrentTurnPlayer?: boolean
    className?: string
    round: ERound
    turnTimeout: number | null
    turnSeconds: number
    communityCards: string | null
    faceDirection?: 'left' | 'right'
    handName?: EHandName | null
    handNumber: number
    winAmount?: number
    isWinner?: boolean
    isSnapshot?: boolean
    onMove?: (move: EAction) => void
    onWin?: () => void
  } & Omit<
    WebSockets.PlayerDataType,
    'lastTurnAt' | 'joinedAt' | 'shouldPostBigBlind'
  > & {
      bigBlind: number
    },
) => {
  const { t } = useTranslation()
  const userSettings = useAtomValue(userSettingsAtom)
  const { value: nameSlot, setImmediately } = useDeferredQueueValue<
    EAction | 'Winner' | null
  >(null, 5_000)
  const prevMoveRef = useRef<EAction | null | undefined>()

  const cards = props.cards?.split?.(' ') || []
  const areCardsRevealed = cards.length && cards[0] !== 'null'
  const handName = useMemo(() => {
    if (props.handName) {
      return props.handName
    }
    if (!areCardsRevealed || !props.cards || !props.communityCards) {
      return null
    }
    const mainEvaluatedHand = evaluateCards(props.cards, props.communityCards)
    return mainEvaluatedHand.handName
  }, [props.handName, props.cards, props.communityCards])

  const nameSlotClasses: Record<EAction | 'Winner', string> = {
    [EAction.CHECK]: styles.blueStatus,
    [EAction.CALL]: styles.blueStatus,
    [EAction.FOLD]: styles.grayStatus,
    [EAction.RAISE]: styles.blueStatus,
    [EAction.ALL_IN]: styles.blueStatus,
    [EAction.BET]: styles.blueStatus,
    Winner: styles.purpleStatus,
  }

  useEffect(() => {
    if (props.move) {
      if (props.move === EAction.ALL_IN) {
        setImmediately(null)
      } else {
        setImmediately(props.move as EAction, props.move !== EAction.FOLD)
      }
      if (
        props.onMove &&
        prevMoveRef.current !== undefined &&
        prevMoveRef.current !== props.move
      ) {
        props.onMove(props.move as EAction)
      }
    } else {
      if (prevMoveRef.current === EAction.FOLD) {
        setImmediately(null)
      }
    }

    prevMoveRef.current = props.move as EAction | null
  }, [props.move])

  useEffect(() => {
    if (props.isWinner) {
      setImmediately('Winner', !props.isSnapshot)
      if (props.onWin) {
        props.onWin()
      }
      return
    }
  }, [props.isWinner])

  const getCards = () => {
    const transformOffset =
      cards.length === 2 ? undefined : cards.length === 5 ? 62.5 : 50

    return cards.map((card, index) => {
      const cardProps = {
        value: card as CardType,
        cardBack: userSettings.tableSettings.cardsBack,
        theme: userSettings.tableSettings.cardsType,
        size: areCardsRevealed ? ('md' as const) : ('sm' as const),
        className: styles.card,
        style:
          transformOffset && index !== 0
            ? { transform: `translateX(-${transformOffset * index}%)` }
            : undefined,
      }

      return props.isSnapshot ? (
        <Card
          key={index}
          {...cardProps}
        />
      ) : (
        <AnimatedCard
          key={index}
          {...cardProps}
          animationType='slideUp'
          direction={props.faceDirection}
        />
      )
    })
  }

  return (
    <div
      className={clsx(
        props.isOnline && props.move !== EAction.FOLD
          ? styles.onlinePlayer
          : styles.offlinePlayer,
        styles.playerGrid,
        props.className,
      )}
      style={{
        zIndex: 0,
      }}
    >
      {handName ? (
        <div className={styles.handNameStyle}>
          {t(`table.handName.${handName}`)}
        </div>
      ) : null}

      <div
        className={styles.avatarSection}
        style={{
          zIndex: 1,
        }}
      >
        <Avatar
          avatar={props.avatar}
          faceDirection={props.faceDirection}
          className={clsx(
            styles.avatar,
            props.isCurrentTurnPlayer && styles.currentTurnPlayer,
          )}
        />

        <div
          className={clsx(
            styles.cardWrapper,
            styles.cardBox,
            styles.cardBoxSize[areCardsRevealed ? 'md' : 'sm'],
            areCardsRevealed && styles.revealedCardBox,
          )}
          style={
            {
              gridTemplateColumns:
                cards.length === 2
                  ? `repeat(${cards.length}, minmax(20%, 1fr))`
                  : `repeat(${cards.length}, 40%)`,
              gap: cards.length > 2 ? 0 : '0.25rem',
            } as React.CSSProperties
          }
        >
          {props.isSnapshot ? (
            getCards()
          ) : (
            <AnimatePresence key={props.handNumber}>
              {getCards()}
            </AnimatePresence>
          )}
        </div>
      </div>

      <div
        className={styles.infoBlock}
        style={{
          zIndex: 2,
        }}
      >
        <span
          className={clsx(styles.name, nameSlot && nameSlotClasses[nameSlot])}
        >
          {nameSlot
            ? nameSlot === 'Winner'
              ? t('table.label.winner')
              : t(`table.action.${nameSlot}`)
            : props.name}
        </span>
        <div className={styles.lineStyle} />
        <span className={styles.buyInStyle}>
          {props.move === EAction.ALL_IN ? (
            t(`table.action.${EAction.ALL_IN}`)
          ) : (
            <Money
              inBigBlinds={userSettings.valuesInBigBlinds}
              bigBlind={props.bigBlind}
            >
              {props.buyIn}
            </Money>
          )}
        </span>

        {props.winAmount ? (
          <div
            className={
              props.faceDirection === 'right'
                ? styles.winAmount
                : styles.winAmountLeft
            }
          >
            +
            <Money
              inBigBlinds={userSettings.valuesInBigBlinds}
              bigBlind={props.bigBlind}
            >
              {props.winAmount}
            </Money>
          </div>
        ) : null}

        {props.turnTimeout !== null && (
          <div
            className={styles.timerBar}
            style={{
              zIndex: 3,
            }}
            key={props.round}
          >
            <CountdownBar
              seconds={props.turnTimeout}
              fullSeconds={props.turnSeconds}
            />
          </div>
        )}
      </div>
    </div>
  )
}
